import { DEFAULT_ALL_VALUE } from '@chronosphereio/core';
import { isEqual } from 'lodash';
import { useMemo } from 'react';
import { SavedView } from '@/core';

/** Sentinel value representing the default view in saved view lists. */
export const DEFAULT_VIEW_VALUE = '____default____';

/** Display label for the default view. */
export const DEFAULT_VIEW_LABEL = 'Default view';

/**
 * Finds saved views that reference a given variable name.
 * @param savedViews - Array of saved views to search
 * @param variableName - Name of the variable to find
 * @returns Array of saved views that contain the variable in their variable_values
 */
export function findSavedViewsWithVariable(savedViews: SavedView[], variableName: string): SavedView[] {
  return savedViews.filter((view) => variableName in view.variable_values);
}

/**
 * Removes a variable from all saved views.
 * @param savedViews - Array of saved views to update
 * @param variableName - Name of the variable to remove
 * @returns New array of saved views with the variable removed from variable_values
 */
export function removeVariableFromSavedViews(savedViews: SavedView[], variableName: string): SavedView[] {
  return savedViews.map((view) => {
    const { [variableName]: _, ...restVariableValues } = view.variable_values;
    return {
      ...view,
      variable_values: restVariableValues,
    };
  });
}

/**
 * Checks if a view name is already taken by an existing saved view or the default view.
 * @param name - The name to check
 * @param savedViews - Array of existing saved views
 * @param defaultViewName - Optional name of the default view
 * @param excludeName - Optional name to exclude from the check (useful when editing a view)
 */
export function isDuplicateViewName(
  name: string,
  savedViews: SavedView[],
  defaultViewName?: string,
  excludeName?: string
): boolean {
  if (!name.trim()) return false;
  const otherViews = excludeName ? savedViews.filter((v) => v.name !== excludeName) : savedViews;
  const existsInSavedViews = otherViews.some((v) => v.name === name.trim());
  const existsInDefault = Boolean(defaultViewName && defaultViewName === name.trim());
  return existsInSavedViews || existsInDefault;
}

/**
 * Generates a default name from variable values by extracting the first 3 valid values.
 * Filters out null, empty strings, and values equal to "all".
 */
export const generateDefaultNameFromValues = (values: SavedView['variable_values']): string => {
  let validValues: string = '';
  let valueCount = 0;

  for (const [, value] of Object.entries(values)) {
    if (value === null) {
      continue;
    }

    // Handle both string and array types
    const valueArray = Array.isArray(value) ? value : [value];

    for (const v of valueArray) {
      // Skip empty strings, null, and "all" values
      if (v && v !== '' && v !== DEFAULT_ALL_VALUE) {
        // Only check length limit if we already have values (allow first value through regardless of length)
        if (validValues) {
          // Check if adding this value would exceed the length limit
          const wouldBeLength = validValues.length + 1 + v.length;
          if (wouldBeLength > 15) {
            // Adding this value would exceed 15, return what we have so far
            return validValues || 'Main view';
          }
        }
        valueCount++;
        validValues = validValues ? `${validValues},${v}` : v;
        // Stop after collecting 3 values or the name is getting too long
        if (valueCount >= 3 || validValues.length >= 15) {
          return validValues || 'Main view';
        }
      }
    }
  }

  return validValues || 'Main view';
};

/**
 * Normalizes single-element arrays to their string value for comparison.
 * This ensures that `["foo"]` and `"foo"` are treated as equivalent.
 */
export const standardizeSingleArrayValue = (value: SavedView['variable_values'][string] | undefined) => {
  if (Array.isArray(value) && value.length === 1) {
    return value[0];
  }

  return value;
};

/**
 * Hook that finds saved views whose variable values match the current variable values.
 * Always includes DEFAULT_VIEW_VALUE as a potential match.
 */
export const useFindMatchingViews = (
  savedViews: SavedView[],
  currentValues: SavedView['variable_values'],
  defaultValues: SavedView['variable_values']
) => {
  const matchingViews = useMemo(() => {
    const defaultViewValues = Object.entries(defaultValues).reduce(
      (acc, [key]) => {
        acc[key] = undefined;
        return acc;
      },
      {} as Record<string, undefined>
    );

    const variableKeys = Object.keys(currentValues);

    // Always include DEFAULT_VIEW_VALUE as a potential match
    const viewsToCheck = [{ name: DEFAULT_VIEW_VALUE, variable_values: defaultViewValues }, ...savedViews];
    return viewsToCheck.filter((view) => {
      return variableKeys.every((key) => {
        const viewValue = standardizeSingleArrayValue(view.variable_values[key]);
        const currentValue = standardizeSingleArrayValue(currentValues[key]);
        const defaultValue = standardizeSingleArrayValue(defaultValues[key]);

        // If the viewValue is not defined, that means it needs to be the default value
        if (viewValue === undefined) {
          return isEqual(currentValue, defaultValue);
        } else {
          // The view value is defined, so current value must be equal to it
          return isEqual(currentValue, viewValue);
        }
      });
    });
  }, [savedViews, currentValues, defaultValues]);

  return matchingViews;
};
